// addAppSaved.js — WHERE A SAVED APP IS, AT ONCE (persona round 1b, BLD-28
// client half: nino, priya, sam, tomas, marcus — "'Add to Workspace' gives no
// message, and the apps list does not show 'Client Portal' afterwards"; "the
// draft app is not findable once I leave the builder"; "after Add to
// Workspace the app moves to the Shared list and the Private list says 'No
// private apps yet'").
//
// After Save, Share or Add to workspace the builder says where the app is now,
// in one plain line with the way to open it, and the app stands in Your apps
// at that moment — never only after a reload or the next list read:
//   addAppSavedRow     the app's row in the person's apps (`_userApps`), held
//                      at once (the list read that follows confirms it), and
//                      the sidebar's place follows the app (`_addAppScope`):
//                      a shared app is under Shared, not missing from Private
//   addAppSavedLink    the way to open it: an app in the workspace opens on its
//                      own page (its navbar tab, `addAppSavedPath`); a private or
//                      shared app opens in its builder thread
//                      (`/add-app?thread=<id>`), unless that thread is already
//                      open. A saved notice must not offer a no-op link.
//   addAppSavedNotice  the builder's line: the sentence and its link
//                      (`_addAppSaveNotice` + `_addAppSaveLink`). The link
//                      belongs to ITS sentence (`notice`), so a later notice
//                      never shows an earlier app's link.
// The person's apps are found by search too (functions/search/
// globalSearch.js reads `_userApps`).
//
// FRANK: every export is self-contained (FA204); siblings through `this.call`.

// The app's row, held at once → the row, or null. `app`: { id, name, scope,
// added }. A row already in the list keeps its own fields (source, pages, …);
// the new name / place and "changed now" are laid over it.
export function addAppSavedRow(root, app) {
  const r = root && typeof root === 'object' ? root : null
  const id = app && app.id != null ? String(app.id) : ''
  if (!r || !id || typeof r.update !== 'function') return null
  const scope = app.scope === 'shared' ? 'shared' : 'private'
  const list = Array.isArray(r._userApps) ? r._userApps : []
  const held = list.find((row) => row && String(row.id) === id) || null
  const name = String((app && app.name) || (held && held.name) || '').trim()
  const row = { ...(held || {}), id, scope, updatedAt: new Date().toISOString() }
  if (name) row.name = name
  const next = held ? list.map((x) => (x && String(x.id) === id ? row : x)) : [row].concat(list)
  r.update({ _userApps: next, _addAppScope: scope }, { preventFetch: true })
  return row
}

// The page an app in the workspace opens on → its path. An extension's page is
// routed at `/x/<key>` (workspaceConfigs.js extensionAppRows): its navbar row
// in the loaded apps (`root.workspaceApps`) carries the real path. `/x/<id>`
// is only the fallback while the app has not loaded yet (persona round 3,
// marcus: "the toast link 'Open Open House Sign-In' went to a 404").
export function addAppSavedPath(root, id) {
  const pid = id != null ? String(id) : ''
  if (!pid) return ''
  const apps = root && Array.isArray(root.workspaceApps) ? root.workspaceApps : []
  const entry = apps.find((m) => m && String(m.id) === pid) || null
  const row = entry && Array.isArray(entry.navbar) ? entry.navbar[0] : null
  const path = row && typeof row.path === 'string' ? row.path : ''
  return path || '/x/' + encodeURIComponent(pid)
}

// The way to open the app → { href, label } (the label in the session
// language: "Open {name}"), or null without an id.
export function addAppSavedLink(app) {
  const id = app && app.id != null ? String(app.id) : ''
  if (!id) return null
  const el = this
  const root = el.state && el.state.root
  const functions = el.context?.functions || {}
  const path = String(root?.currentPath || '').split(/[?#]/)[0]
  const bare =
    typeof functions.stripWorkspacePrefix === 'function'
      ? el.call('stripWorkspacePrefix', path)
      : path
  const builderOpen =
    String(bare || '').replace(/\/+$/, '') === '/add-app' ||
    (typeof functions.homeBuildShown === 'function' && el.call('homeBuildShown', root)) ||
    (typeof functions.homeBuildInPane === 'function' && el.call('homeBuildInPane', root))
  if (!app.added && builderOpen && String(root?._addAppProjectId || '') === id) return null
  const word = (key) => {
    try {
      const v = el.call('polyglot', key)
      return v == null ? '' : String(v)
    } catch (_) {
      return ''
    }
  }
  const name = String((app && app.name) || '').trim() || word('workspace.addApp.extensionFallback')
  return {
    href: app.added
      ? el.call('addAppSavedPath', root, id)
      : '/add-app?thread=' + encodeURIComponent(id),
    label: word('workspace.addApp.save.openApp').replace('{name}', () => name)
  }
}

// The builder's line: `notice` (a resolved sentence) and, for `app`, the link
// that opens it. → the link written, or null.
// The line leaves by itself: 10 s when it carries the way to open the app (a
// 3.5 s toast was missed — "no message shows"), 3.5 s otherwise, and only if
// it is still this line (a newer one owns its own time). The timer is root
// bookkeeping (a plain `__` property, never reactive).
export function addAppSavedNotice(root, notice, app) {
  const r = root && typeof root === 'object' ? root : null
  if (!r || typeof r.update !== 'function' || !notice) return null
  const link = app ? this.call('addAppSavedLink', app) : null
  r.update({
    _addAppSaveNotice: notice,
    _addAppSaveLink: link ? { notice, href: link.href, label: link.label } : null
  })
  if (r.__addAppNoticeTimer) clearTimeout(r.__addAppNoticeTimer)
  r.__addAppNoticeTimer = setTimeout(
    () => {
      r.__addAppNoticeTimer = null
      if (r._addAppSaveNotice === notice) {
        r.update({ _addAppSaveNotice: null, _addAppSaveLink: null })
      }
    },
    link ? 10000 : 3500
  )
  return link
}
